import React from "react";
import { SideTab } from "../../../../components/SideTab";
import { TitleLine } from "../../../../components/TitleLine";
import { Button } from "../../../../components/common/Button";
import { Input } from "../../../../components/common/Input";
import { Box } from "../../styles";
import MapTracking from "../MapTracking";

interface LocationProps {
  id: string;
  accuracy: string;
  altitude: string;
  latitude: number;
  longitude: number;
}

interface VehicleProps {
  id: string;
  vehicle: string;
  color: string;
  plate: string;
  type: string;
  interface: string;
}

interface BaseProps {
  id: string;
  base: string;
  outpost: string;
}

interface DurationProps {
  text: string;
  value: number;
}

interface OccurrenceProps {
  Occurrence: {
    id: string;
    name: string;
    code: string;
    type: string;
    status: string;
    additional_info: string;
    Base?: BaseProps;
    Location: LocationProps;
    created_at: string;
    updated_at: string;
    OccurrenceReview?: {
      id: string;
      id_user: string;
      id_employee: string;
      id_occurrence: string;
      rating: number;
      additional_info: string;
      created_at: string;
    }[];
  };
  Resident: {
    id_user: string;
    name: string;
    email: string;
    phone: string;
    Vehicle: VehicleProps[];
  };
  Employee?: {
    id: string;
    id_user: string;
    status: string;
    type: string;
    name: string;
    email: string;
    phone: string;
    Vehicle: VehicleProps[];
  };
  Vehicle?: VehicleProps;
  Address?: {
    address: string;
    burgh: string;
    city: string;
    state: string;
    latitude: number;
    longitude: number;
    ResponsibleBase: BaseProps;
  };
  durationLocation: DurationProps;
  distanceLocation: DurationProps;
}

interface PanicOccurrenceSidebarProps {
  occurrence: OccurrenceProps;
  isOpen: boolean;
  onClose: () => void;
  timeDuration?: string;
  distanceLocation?: string;
  onTriggerSecurity: () => void;
  isSecuritySideTabOpen: boolean;
}

export function PanicOccurrenceSidebar({
  occurrence,
  isOpen,
  onClose,
  timeDuration,
  distanceLocation,
  onTriggerSecurity,
  isSecuritySideTabOpen,
}: PanicOccurrenceSidebarProps) {
  return (
    <SideTab
      open={isOpen}
      width="40%"
      title={
        "#" +
        occurrence.Occurrence.code +
        " - Ocorrência de " +
        occurrence.Occurrence.type
      }
      toggleFunction={onClose}
    >
      {/* Informações do Morador */}
      <TitleLine title="Informações do Morador" />
      
      {/* Nome | Email | Contato (3-2-1 ratio) */}
      <div style={{ display: "flex", gap: "1rem" }}>
        <Input.Root flex="3">
          <Input.Label title="Nome:" htmlFor="occurrenceName" />
          <Input.Wrapper>
            <Input.Content
              id="occurrenceName"
              type="text"
              value={occurrence.Resident.name}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="2">
          <Input.Label title="Email:" htmlFor="emailInfo" />
          <Input.Wrapper>
            <Input.Content
              id="emailInfo"
              type="text"
              value={occurrence.Resident.email || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Contato:" htmlFor="contactInfo" />
          <Input.Wrapper>
            <Input.Content
              id="contactInfo"
              type="text"
              value={occurrence.Resident.phone || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
      </div>

      {/* Endereço | Horário do envio (2-1 ratio) */}
      <div style={{ display: "flex", gap: "1rem" }}>
        <Input.Root flex="2">
          <Input.Label title="Endereço de envio:" htmlFor="occurrenceAddress" />
          <Input.Wrapper>
            <Input.Content
              id="occurrenceAddress"
              type="text"
              value={occurrence.Address ? `${occurrence.Address.address}, ${occurrence.Address.burgh}, ${occurrence.Address.city} - ${occurrence.Address.state}` : "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Horário de envio:" htmlFor="sendTime" />
          <Input.Wrapper>
            <Input.Content
              id="sendTime"
              type="text"
              value={new Date(occurrence.Occurrence.created_at).toLocaleString()}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
      </div>

      {/* Veículo | Cor do carro | Placa */}
      <div style={{ display: "flex", gap: "1rem" }}>
        <Input.Root flex="1">
          <Input.Label title="Veículo:" htmlFor="userVehicle" />
          <Input.Wrapper>
            <Input.Content
              id="userVehicle"
              type="text"
              value={occurrence.Resident.Vehicle?.[0]?.vehicle || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Cor do carro:" htmlFor="vehicleColor" />
          <Input.Wrapper>
            <Input.Content
              id="vehicleColor"
              type="text"
              value={occurrence.Resident.Vehicle?.[0]?.color || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Placa:" htmlFor="vehiclePlate" />
          <Input.Wrapper>
            <Input.Content
              id="vehiclePlate"
              type="text"
              value={occurrence.Resident.Vehicle?.[0]?.plate || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
      </div>

      {/* Informações do Segurança (Quando disponível / em ocorrência) */}
      {occurrence.Employee && occurrence.Employee.Vehicle && occurrence.Employee.Vehicle.length > 0 && (
        <>
          <TitleLine title="Informações do Segurança" />
          <div style={{ display: "flex", gap: "1rem" }}>
            <Input.Root flex="1">
              <Input.Label title="Segurança:" htmlFor="securityName" />
              <Input.Wrapper>
                <Input.Content
                  id="securityName"
                  type="text"
                  value={occurrence.Employee.name}
                  readOnly
                />
              </Input.Wrapper>
            </Input.Root>
            <Input.Root flex="1">
              <Input.Label title="Veículo:" htmlFor="securityVehicle" />
              <Input.Wrapper>
                <Input.Content
                  id="securityVehicle"
                  type="text"
                  value={occurrence.Employee.Vehicle[0].vehicle}
                  readOnly
                />
              </Input.Wrapper>
            </Input.Root>
            <Input.Root flex="1">
              <Input.Label title="Placa:" htmlFor="securityPlate" />
              <Input.Wrapper>
                <Input.Content
                  id="securityPlate"
                  type="text"
                  value={occurrence.Employee.Vehicle[0].plate}
                  readOnly
                />
              </Input.Wrapper>
            </Input.Root>
          </div>
        </>
      )}

      {/* Mapa do percurso em tempo real */}
      {occurrence.Occurrence.status !== "Fechado" && (
        <>
          <TitleLine title="Mapa do percurso em tempo real" />
          <Box padding="0">
            <MapTracking occurrence={occurrence} />
          </Box>
          
          {/* Ação do momento | Distancia | Tempo previsto de chegada */}
          {occurrence.Occurrence.status !== "Fechado" && (
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
              <Input.Root flex="2">
                <Input.Label title="Ação do momento:" htmlFor="currentAction" />
                <Input.Wrapper>
                  <Input.Content
                    id="currentAction"
                    type="text"
                    value={occurrence.Occurrence.status === "Em ocorrência" ? "Atendendo emergência" : "Aguardando atendimento"}
                    readOnly
                  />
                </Input.Wrapper>
              </Input.Root>
              <Input.Root flex="1">
                <Input.Label title="Distancia:" htmlFor="distance" />
                <Input.Wrapper>
                  <Input.Content
                    id="distance"
                    type="text"
                    value={occurrence.distanceLocation?.text || distanceLocation || "-"}
                    readOnly
                  />
                </Input.Wrapper>
              </Input.Root>
              <Input.Root flex="1">
                <Input.Label
                  title="Tempo previsto de chegada"
                  htmlFor="estimatedTime"
                />
                <Input.Wrapper>
                  <Input.Content
                    id="estimatedTime"
                    type="text"
                    value={occurrence.durationLocation?.text || timeDuration || "-"}
                    readOnly
                  />
                </Input.Wrapper>
              </Input.Root>
            </div>
          )}
        </>
      )}

      {/* Informações adicionais */}
      <TitleLine title="Informações adicionais" />
      <div style={{ display: "flex", gap: "1rem" }}>
        <Input.Root flex="1">
          <Input.Label title="Modalidade:" htmlFor="modality" />
          <Input.Wrapper>
            <Input.Content
              id="modality"
              type="text"
              value={occurrence.Occurrence.type}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Posto:" htmlFor="outpost" />
          <Input.Wrapper>
            <Input.Content
              id="outpost"
              type="text"
              value={occurrence.Occurrence.Base?.outpost || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Status:" htmlFor="status" />
          <Input.Wrapper>
            <Input.Content
              id="status"
              type="text"
              value={occurrence.Occurrence.status}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
      </div>

      {/* Informações detalhadas */}
      {occurrence.Occurrence.additional_info && (
        <Input.Root>
          <Input.Label title="Informações detalhadas:" htmlFor="additionalInfo" />
          <Input.Wrapper>
            <textarea
              id="additionalInfo"
              value={occurrence.Occurrence.additional_info}
              readOnly
              style={{
                width: "100%",
                minHeight: "80px",
                padding: "8px 12px",
                border: "1px solid #CECECE",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
                backgroundColor: "#f9f9f9",
              }}
            />
          </Input.Wrapper>
        </Input.Root>
      )}

      {/* Trigger Security Button for waiting occurrences */}
      {occurrence.Occurrence.status === "Aguardando ação" && (
        <div style={{ alignSelf: "flex-end", minWidth: "15%", maxWidth: "30%", marginTop: "1rem" }}>
          <Button.Root
            type="button"
            backgroundColor="#0079B1"
            onClick={onTriggerSecurity}
          >
            <Button.Content
              title="Acionar um segurança"
              fontSize="12px"
            />
          </Button.Root>
        </div>
      )}

      {/* Show Occurrence Review for closed panic occurrences */}
      {occurrence.Occurrence.status === "Fechado" && (
        <>
          <TitleLine title="Avaliação da Ocorrência" />
          {occurrence.Occurrence.OccurrenceReview &&
          occurrence.Occurrence.OccurrenceReview.length > 0 ? (
            <>
              <div style={{ display: "flex", gap: "1rem" }}>
                <Input.Root flex="1">
                  <Input.Label title="Avaliação:" htmlFor="reviewRating" />
                  <Input.Wrapper>
                    <Input.Content
                      id="reviewRating"
                      type="text"
                      value={`${occurrence.Occurrence.OccurrenceReview?.[0]?.rating || 0}/5`}
                      readOnly
                    />
                  </Input.Wrapper>
                </Input.Root>
                <Input.Root flex="2">
                  <Input.Label title="Data da Avaliação:" htmlFor="reviewDate" />
                  <Input.Wrapper>
                    <Input.Content
                      id="reviewDate"
                      type="text"
                      value={occurrence.Occurrence.OccurrenceReview?.[0]?.created_at ? new Date(
                        occurrence.Occurrence.OccurrenceReview[0].created_at
                      ).toLocaleDateString() : "-"}
                      readOnly
                    />
                  </Input.Wrapper>
                </Input.Root>
              </div>
              <Input.Root>
                <Input.Label title="Comentários:" htmlFor="reviewComments" />
                <Input.Wrapper>
                  <textarea
                    id="reviewComments"
                    value={occurrence.Occurrence.OccurrenceReview?.[0]?.additional_info || ""}
                    readOnly
                    style={{
                      width: "100%",
                      minHeight: "80px",
                      padding: "8px 12px",
                      border: "1px solid #CECECE",
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      resize: "vertical",
                      backgroundColor: "#f9f9f9",
                    }}
                  />
                </Input.Wrapper>
              </Input.Root>
            </>
          ) : (
            <Input.Root>
              <Input.Label title="Status:" htmlFor="noReview" />
              <Input.Wrapper>
                <Input.Content
                  id="noReview"
                  type="text"
                  value="Nenhuma avaliação disponível"
                  readOnly
                />
              </Input.Wrapper>
            </Input.Root>
          )}
        </>
      )}
    </SideTab>
  );
}
